import { Injectable, inject, signal } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { firstValueFrom } from 'rxjs';
import { Category, Product } from '../product.model';
import { CategoryService } from './category.service';
import { ProductService } from './product.service';

@Injectable({ providedIn: 'root' })
export class StorefrontStateService {
  private readonly categoryService = inject(CategoryService);
  private readonly productService = inject(ProductService);

  // ✅ Modern Angular Signals primary state
  private readonly _categories = signal<Category[]>([]);
  readonly categories = this._categories.asReadonly();

  private readonly _selectedIds = signal<number[]>([]);
  readonly selectedIds = this._selectedIds.asReadonly();

  private readonly _products = signal<Product[] | null>(null);
  readonly products = this._products.asReadonly();

  private readonly _loading = signal(false);
  readonly loading = this._loading.asReadonly();

  // Interop Observables
  readonly categories$ = toObservable(this._categories);
  readonly selectedIds$ = toObservable(this._selectedIds);
  readonly products$ = toObservable(this._products);

  private loadPromise: Promise<void> | null = null;

  setCategories(cats: Category[]): void { this._categories.set(cats); }
  getCategories(): Category[] { return this._categories(); }

  setProducts(products: Product[]): void { this._products.set(products); }
  getProducts(): Product[] { return this._products() ?? []; }

  setSelectedIds(ids: number[]): void { this._selectedIds.set(ids); }

  /**
   * Load categories + products once; share in-flight promise across header/shop.
   * Safe to call from multiple entry points.
   */
  ensureLoaded(): Promise<void> {
    if (this._products() !== null) {
      return Promise.resolve();
    }
    if (this.loadPromise) {
      return this.loadPromise;
    }

    this._loading.set(true);
    this.loadPromise = Promise.all([
      firstValueFrom(this.categoryService.getAllCategories()).catch(() => [] as Category[]),
      firstValueFrom(this.productService.getAllProducts()).catch(() => [] as Product[]),
    ])
      .then(([categories, products]) => {
        const cats = categories || [];
        const prods = products || [];
        const categoriesWithCounts = cats.map((cat) => ({
          ...cat,
          product_count: prods.filter((p) =>
            p.categories?.some((c) => c.categories_id === cat.categories_id)
          ).length,
        }));
        this._categories.set(categoriesWithCounts);
        this._products.set(prods);
      })
      .catch(() => {
        this._products.set([]);
      })
      .finally(() => {
        this._loading.set(false);
        this.loadPromise = null;
      });

    return this.loadPromise;
  }

  toggle(id: number): void {
    const current = this._selectedIds();
    const next = current.includes(id)
      ? current.filter(x => x !== id)
      : [...current, id];
    this._selectedIds.set(next);
  }

  clearAll(): void {
    this._selectedIds.set([]);
  }

  refresh(): Promise<void> {
    this._products.set(null);
    return this.ensureLoaded();
  }
}
